import { AUTH_LOGIN_URL, AUTH_LOGOUT_URL, AUTH_SIGNUP_URL } from '@/config/auth'
import { WEBSITE_URL } from '@/config/system'
import { ApiError } from '@/services/api'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import CancelOutlined from '@mui/icons-material/CancelOutlined'
import LockOutlined from '@mui/icons-material/LockOutlined'
import SecurityOutlined from '@mui/icons-material/SecurityOutlined'
import WarningOutlined from '@mui/icons-material/WarningOutlined'
import * as Sentry from '@sentry/react'
import { type ReactNode } from 'react'
import { ErrorInfo } from 'react-dom/client'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { useLocation } from 'react-router-dom'
import { useUserContext } from '../contexts/userContext'

type ErrorFallbackProps = {
  error: Error
  resetErrorBoundary: () => void
}

const ErrorFallback = ({
  error,
  resetErrorBoundary: _resetErrorBoundary,
}: ErrorFallbackProps) => {
  const handleLogin = () => {
    window.location.href =
      AUTH_LOGIN_URL +
      '?product=ai-business' +
      '&redirectTo=' +
      encodeURIComponent(window.location.href)
  }

  const handleSignUp = () => {
    window.location.href =
      AUTH_SIGNUP_URL +
      '?product=ai-business' +
      '&redirectTo=' +
      encodeURIComponent(window.location.href)
  }

  const handleGoHome = () => {
    window.location.href = WEBSITE_URL
  }

  const handleLogout = () => {
    window.location.href =
      AUTH_LOGOUT_URL + '?redirectTo=' + encodeURIComponent(WEBSITE_URL)
  }

  const apiError =
    error instanceof ApiError
      ? error
      : new ApiError(
          'An unexpected error occurred',
          0,
          error.message || 'An unexpected error occurred'
        )

  const { statusCode, message } = apiError

  if (statusCode === 401) {
    return (
      <Dialog open>
        <DialogContent className="sm:max-w-md" hideCloseButton>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <LockOutlined sx={{ fontSize: 20 }} className="text-error" />
              <DialogTitle>Authentication Required</DialogTitle>
            </div>
            <DialogDescription>
              You need to be logged in to access this resource. Please sign in
              to continue.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button onClick={handleLogin}>Sign In</Button>
            <Button
              id="error-boundary-button--redirect-to-signup"
              variant="ghost"
              onClick={handleSignUp}
            >
              Sign Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  if (statusCode === 403) {
    return (
      <Dialog open>
        <DialogContent className="sm:max-w-md" hideCloseButton>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <SecurityOutlined sx={{ fontSize: 20 }} className="text-error" />
              <DialogTitle>Access Denied</DialogTitle>
            </div>
            <DialogDescription>
              You don&apos;t have permission to access this resource. Please
              contact your administrator if you believe this is an error.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button onClick={handleGoHome}>Go to Home</Button>
            <Button onClick={handleLogout}>Logout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  if (statusCode >= 500) {
    return (
      <Dialog open>
        <DialogContent className="sm:max-w-md" hideCloseButton>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <CancelOutlined sx={{ fontSize: 20 }} className="text-error" />
              <DialogTitle>Server Error</DialogTitle>
            </div>
            <DialogDescription>
              {message ||
                'An unexpected error occurred. Please try again later or contact support if the problem persists.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button onClick={handleGoHome}>Go to Home</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open>
      <DialogContent className="sm:max-w-md" hideCloseButton>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <WarningOutlined sx={{ fontSize: 20 }} className="text-error" />
            <DialogTitle>Error</DialogTitle>
          </div>
          <DialogDescription>
            {message || 'An error occurred while processing your request.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button onClick={handleGoHome}>Go to Home</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type ErrorBoundaryProps = {
  children: ReactNode
}

export const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const location = useLocation()
  const { user } = useUserContext()

  const logError = (error: Error, errorInfo: ErrorInfo) => {
    if (
      error instanceof ApiError &&
      error.statusCode >= 400 &&
      error.statusCode < 500
    ) {
      return
    }

    Sentry.setUser(
      user?._id
        ? {
            id: user._id,
            email: user.email,
          }
        : null
    )
    Sentry.setContext('errorInfo', {
      componentStack: errorInfo?.componentStack ?? '',
      digest: errorInfo?.digest ?? '',
    })

    Sentry.captureException(error)
  }

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={logError}
      resetKeys={[location.pathname]} // Reset error boundary when location changes
    >
      {children}
    </ReactErrorBoundary>
  )
}

export default ErrorBoundary
